/* eslint-disable import-x/no-namespace */

import { useGetLists } from '@vlabs/api/luna-api/client';
import type { GetCardsFilterKeys, PointGetCardsFilters } from '@vlabs/api/types';

import { createFastFilterDateLabel } from '@vlabs/monokit/shared/lib/fast-filter-date-label';
import { FastFilterGroup } from '@vlabs/monokit/shared/ui/fast-filter-group';

import * as fastFilters from './fast-filters';
import { hasIntersection } from './hasIntersections';
import { useCardsFilters } from './useCardsFilters';
import { FIO_FILTER_KEY } from './utils';

interface CardListFastFiltersProps {
  exclude?: (keyof PointGetCardsFilters)[];
}

function CardListFastFilters({
  exclude = undefined,
}: CardListFastFiltersProps): JSX.Element {
  const { filters, updateFilters } = useCardsFilters();
  const getLists = useGetLists({ list_ids: filters.list_ids }, {
    query: {
      enabled: (filters?.list_ids?.length ?? 0) > 0,
    },
  });
  const listOptions = getLists.data?.lists?.map((list) => ({ label: list.user_data, value: list.list_id })) ?? [];

  const isExcluded = (filterKeys: GetCardsFilterKeys) => hasIntersection<GetCardsFilterKeys>(exclude, filterKeys);

  const getListsName = (arr: string[]): string[] => {
    if (!arr?.length || !listOptions?.length) return [];

    return arr.map((id) => {
      const option = listOptions.find((list) => list.value === id);
      return option?.label ?? id;
    });
  };

  return (
    <FastFilterGroup>
      {Object.entries(filters).map(([name, value]) => {
        if (value === undefined) return undefined;
        if (isExcluded([name] as GetCardsFilterKeys)) return undefined;
        switch (name) {
          case 'card_ids': return (
            <fastFilters.CardIdFilterWithBottomPopover
              data-testid={name}
              key={name}
              onClear={() => updateFilters({ card_ids: undefined })}
              value={value}
            />
          );
          case FIO_FILTER_KEY: return (
            <fastFilters.FioFilterWithBottomPopover
              data-testid={name}
              key={name}
              onClear={() => updateFilters({ [FIO_FILTER_KEY]: undefined })}
              value={value}
            />
          );
          case 'list_ids': return (
            <fastFilters.ListsFilterWithBottomPopover
              data-testid={name}
              key={name}
              onClear={() => updateFilters({ list_ids: undefined })}
              value={getListsName(value)}
            />
          );
          default: return undefined;
        }
      })}
      {!!(filters.create_time__gte ?? filters.create_time__lt) && (
        <fastFilters.DateFilterWithBottomPopover
          data-testid="create_time"
          key="create_time"
          onClear={() => updateFilters({ create_time__gte: undefined, create_time__lt: undefined })}
          value={createFastFilterDateLabel({
            createTimeGte: filters.create_time__gte,
            createTimeLt: filters.create_time__lt,
          })}
        />
      )}
    </FastFilterGroup>
  );
}

export { CardListFastFilters };
